import { useAutoneTranslation } from '@autone/translations';
import { DialogTrigger } from '@autone/ui';
import {
  ActionBar,
  ActionBarItem,
  ActionBarSeparator,
  Button,
  Dialog,
} from '@autone/ui-next/atoms';
import { usePaginatedSelectionContext } from '@autone/ui-next/components';
import {
  AssortItemIcon,
  AutoneIcon,
  CancelIcon,
  UnassortItemIcon,
} from '@autone/ui-next/icons';
import { ShirtIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { type APP_NAME } from '../../../../consts';

import { SkuLocationsTableAssortUnassortModalContent } from './SkuLocationsTableAssortUnassortModalContent';
import { SkuLocationsTableAutomateAssortmentModalContent } from './SkuLocationsTableAutomateAssortmentModalContent';
import { SkuLocationsTableInitialAllocationModalContent } from './SkuLocationsTableInitialAllocationModalContent';

export const SkuLocationsTableActionBar = () => {
  const { common, t } = useAutoneTranslation<typeof APP_NAME>();
  const [state, controls] = usePaginatedSelectionContext();

  // modal states
  const [isIaOpen, setIsIaOpen] = useState(false);
  const [isAssortAllOpen, setIsAssortAllOpen] = useState(false);
  const [isUnassortAllOpen, setIsUnassortAllOpen] = useState(false);
  const [isAutomateAssortmentOpen, setIsAutomateAssortmentOpen] =
    useState(false);

  const { selectedRowCount } = state;
  const hasSelections = state.getIsAllRowsSelected() !== 'none';

  const handleBulkEditSuccess = useCallback((result: 'ok' | 'error') => {
    if (result === 'error') return;
    setIsIaOpen(false);
    setIsAssortAllOpen(false);
    setIsUnassortAllOpen(false);
    setIsAutomateAssortmentOpen(false);
  }, []);

  return (
    <ActionBar show={hasSelections}>
      <ActionBarItem asChild>
        <Button
          id={'close-action-bar-button'}
          aria-label={common('close')}
          variant={'ghost'}
          onClick={controls.clearSelection}
        >
          <CancelIcon />
          {selectedRowCount.toLocaleString()} {common('selected')}
        </Button>
      </ActionBarItem>
      <ActionBarSeparator />
      <Dialog open={isIaOpen} onOpenChange={setIsIaOpen}>
        <ActionBarItem asChild>
          <DialogTrigger asChild>
            <Button
              id={'assort-all-button'}
              aria-label={t('action-bar.set-ia')}
              variant={'ghost'}
            >
              <ShirtIcon className="size-4" />
              {t('action-bar.set-ia')}
            </Button>
          </DialogTrigger>
        </ActionBarItem>
        <SkuLocationsTableInitialAllocationModalContent
          onEditComplete={handleBulkEditSuccess}
        />
      </Dialog>
      <Dialog open={isAssortAllOpen} onOpenChange={setIsAssortAllOpen}>
        <ActionBarItem asChild>
          <DialogTrigger asChild>
            <Button
              id={'assort-all-button'}
              aria-label={t('action-bar.assort-all')}
              variant={'ghost'}
            >
              <AssortItemIcon />
              {t('action-bar.assort-all')}
            </Button>
          </DialogTrigger>
        </ActionBarItem>
        <SkuLocationsTableAssortUnassortModalContent
          mode={'assort'}
          onEditComplete={handleBulkEditSuccess}
        />
      </Dialog>
      <Dialog open={isUnassortAllOpen} onOpenChange={setIsUnassortAllOpen}>
        <ActionBarItem asChild>
          <DialogTrigger asChild>
            <Button
              id={'unassort-all-button'}
              aria-label={t('action-bar.unassort-all')}
              variant={'ghost'}
            >
              <UnassortItemIcon />
              {t('action-bar.unassort-all')}
            </Button>
          </DialogTrigger>
        </ActionBarItem>
        <SkuLocationsTableAssortUnassortModalContent
          mode={'unassort'}
          onEditComplete={handleBulkEditSuccess}
        />
      </Dialog>
      <Dialog
        open={isAutomateAssortmentOpen}
        onOpenChange={setIsAutomateAssortmentOpen}
      >
        <ActionBarItem asChild>
          <DialogTrigger asChild>
            <Button
              id={'automate-assortment-button'}
              aria-label={t('action-bar.automate-assortment')}
              variant={'ghost'}
            >
              <AutoneIcon className="size-4" />
              {t('action-bar.automate-assortment')}
            </Button>
          </DialogTrigger>
        </ActionBarItem>
        <SkuLocationsTableAutomateAssortmentModalContent
          onEditComplete={handleBulkEditSuccess}
        />
      </Dialog>
    </ActionBar>
  );
};
